import React from 'react';
import { Divider, Popover, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { useUnit } from 'effector-react';
import { Link } from 'react-router-dom';
import {
  $mgpMaps,
  $mgpPageFiltered,
  $mgpTypesCount,
  getMGPPageListFx,
  MGPsListGate,
  setMgpTypeFilterEv,
} from '../../../models/mgpModel/index.js';
import MGPFilters from './MGPFilters.jsx';
import styles from '../MGPRegistry.module.css';
import {
  $activitiesMap,
  $regionsMap,
} from '../../../models/globalModel/index.js';

function MgpContents() {
  const { mgpMap, kspMap, typeMap, kindMap, targetMap, operatorsMap } =
    useUnit($mgpMaps);
  const listData = useUnit($mgpPageFiltered);
  const typeCounts = useUnit($mgpTypesCount);
  const changeType = useUnit(setMgpTypeFilterEv);
  const loading = useUnit(getMGPPageListFx.pending);
  const activitiesMap = useUnit($activitiesMap);
  const regionsMap = useUnit($regionsMap);
  const columns = [
    {
      title: 'МГП',
      dataIndex: 'program_id',
      key: 'mgp_id',
      width: 200,
      render: (id, record) => (
        <Tooltip title={mgpMap[id]} placement="topLeft">
          <Link to={record.link}>{mgpMap[id]}</Link>
        </Tooltip>
      ),
    },
    {
      title: 'Регион',
      dataIndex: 'region_ids',
      key: 'region',
      width: 150,
      render: (ids) =>
        ids.length === 20 ? (
          <Tag>Весь Казахстан</Tag>
        ) : (
          <Popover
            content={
              <div style={{ maxWidth: 400 }}>
                {ids?.map((id) => (
                  <Tag key={id}>{regionsMap[id]}</Tag>
                ))}
              </div>
            }
            placement="bottom"
          >
            <Tag>
              {ids?.length > 1
                ? `${regionsMap[ids[0]]}...`
                : regionsMap[ids[0]]}
            </Tag>
          </Popover>
        ),
    },
    {
      title: 'ОКЭД',
      dataIndex: 'activity_ids',
      key: 'oked',
      width: 200,
      render: (ids) => {
        if (ids) {
          return (
            <Popover
              content={
                <div
                  style={{
                    display: 'flex',
                    gap: 4,
                    maxWidth: 800,
                    maxHeight: 400,
                    overflow: 'auto',
                    flexWrap: 'wrap',
                  }}
                >
                  {ids.map((id) => (
                    <Tag>{activitiesMap[id]}</Tag>
                  ))}
                </div>
              }
              placement="bottom"
            >
              <Tag style={{ maxWidth: 200 }}>
                <Typography.Paragraph ellipsis>
                  {activitiesMap[ids[0]]}
                </Typography.Paragraph>
              </Tag>
            </Popover>
          );
        }
        return null;
      },
    },
    {
      title: 'КСП',
      dataIndex: 'ksp_ids',
      key: 'category',
      width: 200,
      render: (ids) =>
        ids && ids?.length > 0
          ? ids?.map((id) => <Tag key={id}>{kspMap[id]}</Tag>)
          : '',
    },
    {
      title: 'Тип поддержки',
      dataIndex: 'type_id',
      key: 'type',
      render: (id) => typeMap[id] ?? '',
    },
    {
      title: 'Вид поддержки',
      dataIndex: 'kind_id',
      key: 'kind',
      render: (id) => kindMap[id] ?? '',
    },
    {
      title: 'Цель',
      dataIndex: 'target_ids',
      key: 'target',
      render: (ids) => {
        if (ids) {
          return ids?.map((id) => <Tag key={id}>{targetMap[id]}</Tag>);
        }
        return null;
      },
    },
    {
      title: 'Сумма',
      width: 300,
      key: 'sum',
      children: [
        {
          title: 'От',
          dataIndex: `sum_from`,
          render: (value) =>
            new Intl.NumberFormat('ru-RU', { notation: 'compact' }).format(
              value
            ),
        },
        {
          title: 'До',
          dataIndex: `sum_to`,
          render: (value) =>
            new Intl.NumberFormat('ru-RU', { notation: 'compact' }).format(
              value
            ),
        },
      ],
    },
    {
      title: 'Ставка',
      width: 300,
      key: 'rate',
      children: [
        {
          title: 'От',
          dataIndex: `rate_from`,
          render: (value) =>
            new Intl.NumberFormat('ru-RU', { notation: 'compact' }).format(
              value
            ),
        },
        {
          title: 'До',
          dataIndex: `rate_to`,
          render: (value) =>
            new Intl.NumberFormat('ru-RU', { notation: 'compact' }).format(
              value
            ),
        },
      ],
    },
    {
      title: 'Сроки, мес.',
      key: 'deadline',
      width: 300,
      children: [
        {
          title: 'От',
          dataIndex: `deadline_from`,
          render: (value) =>
            new Intl.NumberFormat('ru-RU', { notation: 'compact' }).format(
              value
            ),
        },
        {
          title: 'До',
          dataIndex: `deadline_to`,
          render: (value) =>
            new Intl.NumberFormat('ru-RU', { notation: 'compact' }).format(
              value
            ),
        },
      ],
    },
    {
      title: 'Оператор',
      dataIndex: 'operator',
      key: 'operator',
      width: 200,
      render: (id) => (
        <Tooltip title={operatorsMap[id] ?? ''} placement="topLeft">
          {operatorsMap[id] ?? ''}
        </Tooltip>
      ),
    },
    {
      title: 'Описание',
      dataIndex: 'description',
      key: 'description',
      width: 100,
      render: (text) => (
        <Tooltip title={text}>
          <Typography.Paragraph ellipsis style={{ maxWidth: 100 }}>
            {text}
          </Typography.Paragraph>
        </Tooltip>
      ),
    },
    {
      title: 'Ссылка',
      dataIndex: 'link',
      key: 'link',
      render: (link) => (
        <Link to={link} target="_blank">
          {link}
        </Link>
      ),
    },
  ];

  return (
    <>
      <MGPsListGate />
      <MGPFilters />
      <Divider
        style={{ marginTop: 0 }}
        orientationMargin={0}
        orientation="left"
      >
        По виду поддержки
      </Divider>
      <Space direction="horizontal" style={{ gap: 16, marginBottom: 16 }}>
        <div onClick={() => changeType(1)} className={styles.typeSelect}>
          <span>
            <strong>Финансовая</strong>
          </span>
          <span>{typeCounts.fin}</span>
        </div>
        <div onClick={() => changeType(2)} className={styles.typeSelect}>
          <span>
            <strong>Нефинансовая</strong>
          </span>
          <span>{typeCounts.non_fin}</span>
        </div>
        <div onClick={() => changeType(null)} className={styles.typeSelect}>
          <span>
            <strong>Всего</strong>
          </span>
          <span>{typeCounts.all}</span>
        </div>
      </Space>
      <Table
        columns={columns}
        dataSource={listData}
        size="small"
        loading={loading}
        bordered
        rowClassName="table-row"
        style={{ width: '100%', overflow: 'auto' }}
      />
    </>
  );
}

export default MgpContents;
